import { useContext } from 'react';
import { useSearchParams } from 'react-router';

import { parseRollbackSearchParams } from './internal/parseRollbackSearchParams';
import { RollbackContext } from './RollbackContext';
import type { RollbackContextValue } from './types';

/**
 * Hook to access rollback context.
 * Returns the rollback context value or null when:
 * - Not in rollback mode (no rollbackId in URL)
 * - Data is still loading
 * - An error occurred fetching the audit log entry
 *
 * @example
 * ```tsx
 * function TargetingEditor() {
 *   const rollback = useRollbackContext();
 *   if (rollback) {
 *     // Pre-populate form with rollback.previousVersion
 *   }
 * }
 * ```
 */
export function useRollbackContext(): RollbackContextValue | null {
  return useContext(RollbackContext);
}

/**
 * Hook to get the rollback ID if present in the URL.
 * Returns the previewId string or null if not in rollback mode.
 *
 * @example
 * ```tsx
 * function RollbackBanner() {
 *   const previewId = useRollbackId();
 *   if (!previewId) return null;
 *   return <Banner>Previewing rollback for entry {previewId}</Banner>;
 * }
 * ```
 */
export function useRollbackId(): string | null {
  const [searchParams] = useSearchParams();
  const { previewId } = parseRollbackSearchParams(searchParams);
  return previewId;
}
